import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    DEFAULT_PAGE_SIZE,
    getTablePageSizeStorageKey,
    resolveTablePageSizePreference,
    saveTablePageSizeToStorage,
} from "helpers/GeneralHelper";
import {
    saveTablePageSizeUserSetting,
} from "model/UserSettingModel";
import { useUserSettingsSync } from "libs/context/HeaderSettingsSyncContext";

const TABLE_PAGE_SIZE_SYNC_DEBOUNCE_MS = 400;

const TablePageSizePreferencesContext = createContext(null);

export const TablePageSizePreferencesProvider = ({ children }) => {
    const { settingsReady, syncGeneration } = useUserSettingsSync();
    const [pageSizeByTableKey, setPageSizeByTableKey] = useState({});
    const serverSyncTimeoutByTableKeyRef = useRef({});

    useEffect(
        () => () => {
            Object.values(serverSyncTimeoutByTableKeyRef.current).forEach(clearTimeout);
            serverSyncTimeoutByTableKeyRef.current = {};
        },
        []
    );

    /** Re-read localStorage after the shared UserSetting sync. */
    useEffect(() => {
        if (syncGeneration < 1) return;
        setPageSizeByTableKey({});
    }, [syncGeneration]);

    const syncPageSizeToServer = useCallback((tableKey, pageSize) => {
        if (!tableKey) return;

        const timeouts = serverSyncTimeoutByTableKeyRef.current;
        if (timeouts[tableKey]) {
            clearTimeout(timeouts[tableKey]);
        }

        timeouts[tableKey] = setTimeout(() => {
            delete timeouts[tableKey];
            saveTablePageSizeUserSetting(
                getTablePageSizeStorageKey(tableKey),
                pageSize
            );
        }, TABLE_PAGE_SIZE_SYNC_DEBOUNCE_MS);
    }, []);

    const setPageSize = useCallback(
        (tableKey, pageSize, { syncToServer = true } = {}) => {
            if (!tableKey) return;

            const normalizedPageSize = Number(pageSize);
            if (!Number.isFinite(normalizedPageSize)) return;

            saveTablePageSizeToStorage(tableKey, normalizedPageSize);
            setPageSizeByTableKey((previous) => ({
                ...previous,
                [tableKey]: normalizedPageSize,
            }));

            if (syncToServer) {
                syncPageSizeToServer(tableKey, normalizedPageSize);
            }
        },
        [syncPageSizeToServer]
    );

    const getPageSize = useCallback(
        (tableKey, defaultPageSize = DEFAULT_PAGE_SIZE) => {
            if (!tableKey) return defaultPageSize;

            const fromMemory = pageSizeByTableKey[tableKey];
            if (Number.isFinite(fromMemory)) {
                return fromMemory;
            }

            return resolveTablePageSizePreference(tableKey, defaultPageSize);
        },
        [pageSizeByTableKey]
    );

    const value = useMemo(
        () => ({
            pageSizeByTableKey,
            settingsReady,
            getPageSize,
            setPageSize,
        }),
        [pageSizeByTableKey, settingsReady, getPageSize, setPageSize]
    );

    return (
        <TablePageSizePreferencesContext.Provider value={value}>
            {children}
        </TablePageSizePreferencesContext.Provider>
    );
};

export const useTablePageSizePreference = (
  tableKey = "",
  defaultPageSize = DEFAULT_PAGE_SIZE
) => {
    const context = useContext(TablePageSizePreferencesContext);
    const resolvedTableKey = String(tableKey ?? "").trim();
    const storedPageSize = context?.pageSizeByTableKey?.[resolvedTableKey];

    const pageSize = useMemo(() => {
        if (!resolvedTableKey) return defaultPageSize;
        if (context) {
            return context.getPageSize(resolvedTableKey, defaultPageSize);
        }
        return resolveTablePageSizePreference(resolvedTableKey, defaultPageSize);
    }, [context, resolvedTableKey, defaultPageSize, storedPageSize]);

    const setPageSize = useCallback(
        (nextPageSize) => {
            if (!resolvedTableKey) return;
            if (context) {
                context.setPageSize(resolvedTableKey, nextPageSize);
                return;
            }
            saveTablePageSizeToStorage(resolvedTableKey, nextPageSize);
        },
        [context, resolvedTableKey]
    );

    const settingsReady = context ? Boolean(context.settingsReady) : true;

    return {
        pageSize,
        setPageSize,
        settingsReady,
    };
};

export default TablePageSizePreferencesContext;
